/**
 * Author: Claude
 * Generation: Agent Battle Royale 3.0 - Frame Animation Configuration
 * Version: v3.1.0
 * Modified: 2025-09-03 Beijing Time
 */

import { 
  AnimationType, 
  BaseAnimationType, 
  WeaponAnimationType, 
  WeaponType,
  FrameAnimationConfig,
  FRAME_ANIMATION_PRIORITY,
  ANIMATION_DURATION,
  ANIMATION_FRAMERATE 
} from './types';

// 游戏事件到动画类型映射
export const GAME_EVENT_TO_ANIMATION = {
  // Agent基础事件
  'agent:moved': 'walk',
  'agent:hurt': 'hurt', 
  'agent:died': 'die',
  'agent:idle': 'idle',
  'agent:using_item': 'use_item',
  'agent:rushing': 'rush',
  
  // 战斗事件（动态处理，基于武器类型）
  'combat:started': 'idle',
  'combat:damage_dealt': 'dynamic', // 根据武器类型动态选择
  'combat:attack_missed': 'dynamic', // 根据武器类型动态选择
  'combat:critical_hit': 'dynamic', // 根据武器类型动态选择 
  'combat:ended': 'idle'
} as const;

// 史莱姆角色类型（支持全部7种颜色）
export type SlimeCharacterType = 'green' | 'red' | 'blue' | 'black' | 'white' | 'orange' | 'purple';

// 史莱姆角色配置（全部7种颜色）
export const SLIME_CHARACTER_CONFIG: Record<SlimeCharacterType, string> = {
  green: 'assets/characters/green',
  red: 'assets/characters/red',
  blue: 'assets/characters/blue',
  black: 'assets/characters/black',
  white: 'assets/characters/white',
  orange: 'assets/characters/orange',
  purple: 'assets/characters/purple'
};

// 颜色ID映射（对应素材文件中的编号）
const COLOR_ID_MAP: Record<SlimeCharacterType, string> = {
  green: '0',
  red: '1',
  purple: '2',
  blue: '3',
  white: '4',
  black: '5',
  orange: '6'
};

// 根据史莱姆类型和动画生成帧路径的工具函数
function generateSlimeFrames(character: SlimeCharacterType, animationType: string, frameCount: number, frameFormat: string = '_%d'): string[] {
  const basePath = SLIME_CHARACTER_CONFIG[character];
  const slimeId = COLOR_ID_MAP[character];
  const frames: string[] = [];

  for (let i = 0; i < frameCount; i++) {
    const frameNum = frameFormat === '_00' ? i.toString().padStart(2, '0') : i.toString();
    frames.push(`${basePath}/像素史莱姆-${slimeId}-${animationType}_${frameNum}.png`);
  }

  return frames;
}

// 根据Agent ID选择史莱姆角色类型
export function getSlimeCharacterType(agentId: string): SlimeCharacterType {
  // Alice使用绿色史莱姆，Bob使用红色史莱姆
  if (agentId.includes('alice') || agentId === 'agent_alice') {
    return 'green';
  } else if (agentId.includes('bob') || agentId === 'agent_bob') {
    return 'red';
  }

  // 根据agent的color属性直接映射（GamePage创建的demo agents有color字段）
  // 从agentId中提取颜色信息（如果存在）
  const colorMatch = agentId.match(/_(white|purple|orange|blue|black|green|red)_/i);
  if (colorMatch) {
    return colorMatch[1].toLowerCase() as SlimeCharacterType;
  }

  // 默认根据agent数量分配颜色
  return Math.random() > 0.5 ? 'green' : 'red';
}

// 根据Agent获取特定角色的动画配置
export function getCharacterAnimationConfig(agentId: string, animationType: BaseAnimationType): FrameAnimationConfig {
  const character = getSlimeCharacterType(agentId);
  const baseConfig = BASE_ANIMATION_ASSETS[animationType];
  
  // 根据角色类型生成对应的帧序列
  let frames: string[];
  switch (animationType) {
    case 'idle':
      frames = generateSlimeFrames(character, 'idle', 11, '_00');
      break;
    case 'walk':
    case 'move':
      frames = generateSlimeFrames(character, 'mov', 8);
      break;
    case 'hurt':
      frames = generateSlimeFrames(character, 'hurt', 3);
      break;
    case 'rush':
      frames = generateSlimeFrames(character, 'rush', 11, '_00');
      break;
    case 'use_item':
      frames = generateSlimeFrames(character, 'use', 11, '_00');
      break;
    case 'die':
      frames = generateSlimeFrames(character, 'die', 12, '_00');
      break;
    default:
      frames = baseConfig.frames;
  }
  
  return {
    ...baseConfig,
    key: `${character}_${baseConfig.key}`,
    frames
  };
}

// 根据Agent获取特定角色的武器攻击动画配置
export function getCharacterWeaponAnimationConfig(agentId: string, weaponType: WeaponType): FrameAnimationConfig {
  const character = getSlimeCharacterType(agentId);
  const baseConfig = WEAPON_ANIMATION_ASSETS[weaponType];
  const frames = generateSlimeFrames(character, 'atk', 8);
  
  return {
    ...baseConfig,
    key: `${character}_${baseConfig.key}`,
    frames
  };
}

// 序列帧资源配置 - 基础动画（使用真实史莱姆素材）
export const BASE_ANIMATION_ASSETS: Record<BaseAnimationType, FrameAnimationConfig> = {
  idle: {
    key: 'slime_idle',
    type: 'idle',
    frames: [
      'assets/characters/green/像素史莱姆-0-idle_00.png',
      'assets/characters/green/像素史莱姆-0-idle_01.png',
      'assets/characters/green/像素史莱姆-0-idle_02.png',
      'assets/characters/green/像素史莱姆-0-idle_03.png',
      'assets/characters/green/像素史莱姆-0-idle_04.png',
      'assets/characters/green/像素史莱姆-0-idle_05.png',
      'assets/characters/green/像素史莱姆-0-idle_06.png',
      'assets/characters/green/像素史莱姆-0-idle_07.png',
      'assets/characters/green/像素史莱姆-0-idle_08.png',
      'assets/characters/green/像素史莱姆-0-idle_09.png',
      'assets/characters/green/像素史莱姆-0-idle_10.png'
    ],
    duration: ANIMATION_DURATION.idle,
    frameRate: ANIMATION_FRAMERATE.idle,
    loop: true,
    priority: FRAME_ANIMATION_PRIORITY.IDLE
  },
  
  walk: {
    key: 'slime_walk',
    type: 'walk',
    frames: [
      'assets/characters/green/像素史莱姆-0-mov_0.png',
      'assets/characters/green/像素史莱姆-0-mov_1.png',
      'assets/characters/green/像素史莱姆-0-mov_2.png',
      'assets/characters/green/像素史莱姆-0-mov_3.png',
      'assets/characters/green/像素史莱姆-0-mov_4.png',
      'assets/characters/green/像素史莱姆-0-mov_5.png',
      'assets/characters/green/像素史莱姆-0-mov_6.png',
      'assets/characters/green/像素史莱姆-0-mov_7.png'
    ],
    duration: ANIMATION_DURATION.walk,
    frameRate: ANIMATION_FRAMERATE.walk,
    loop: true,
    priority: FRAME_ANIMATION_PRIORITY.WALK
  },
  
  move: {  // 兼容旧系统 - 使用walk动画
    key: 'slime_move',
    type: 'move', 
    frames: [
      'assets/characters/green/像素史莱姆-0-mov_0.png',
      'assets/characters/green/像素史莱姆-0-mov_1.png',
      'assets/characters/green/像素史莱姆-0-mov_2.png',
      'assets/characters/green/像素史莱姆-0-mov_3.png',
      'assets/characters/green/像素史莱姆-0-mov_4.png',
      'assets/characters/green/像素史莱姆-0-mov_5.png',
      'assets/characters/green/像素史莱姆-0-mov_6.png',
      'assets/characters/green/像素史莱姆-0-mov_7.png'
    ],
    duration: ANIMATION_DURATION.move,
    frameRate: ANIMATION_FRAMERATE.move,
    loop: true,
    priority: FRAME_ANIMATION_PRIORITY.WALK
  },
  
  hurt: {
    key: 'slime_hurt',
    type: 'hurt',
    frames: [
      'assets/characters/green/像素史莱姆-0-hurt_0.png',
      'assets/characters/green/像素史莱姆-0-hurt_1.png',
      'assets/characters/green/像素史莱姆-0-hurt_2.png'
    ],
    duration: ANIMATION_DURATION.hurt,
    frameRate: ANIMATION_FRAMERATE.hurt,
    loop: false,
    priority: FRAME_ANIMATION_PRIORITY.HURT
  },
  
  rush: {
    key: 'slime_rush',
    type: 'rush',
    frames: [
      'assets/characters/green/像素史莱姆-0-rush_00.png',
      'assets/characters/green/像素史莱姆-0-rush_01.png',
      'assets/characters/green/像素史莱姆-0-rush_02.png',
      'assets/characters/green/像素史莱姆-0-rush_03.png',
      'assets/characters/green/像素史莱姆-0-rush_04.png',
      'assets/characters/green/像素史莱姆-0-rush_05.png',
      'assets/characters/green/像素史莱姆-0-rush_06.png',
      'assets/characters/green/像素史莱姆-0-rush_07.png',
      'assets/characters/green/像素史莱姆-0-rush_08.png',
      'assets/characters/green/像素史莱姆-0-rush_09.png',
      'assets/characters/green/像素史莱姆-0-rush_10.png'
    ],
    duration: ANIMATION_DURATION.rush,
    frameRate: ANIMATION_FRAMERATE.rush,
    loop: false,
    priority: FRAME_ANIMATION_PRIORITY.RUSH
  },
  
  use_item: {
    key: 'slime_use_item',
    type: 'use_item',
    frames: [
      'assets/characters/green/像素史莱姆-0-use_00.png',
      'assets/characters/green/像素史莱姆-0-use_01.png',
      'assets/characters/green/像素史莱姆-0-use_02.png',
      'assets/characters/green/像素史莱姆-0-use_03.png',
      'assets/characters/green/像素史莱姆-0-use_04.png',
      'assets/characters/green/像素史莱姆-0-use_05.png',
      'assets/characters/green/像素史莱姆-0-use_06.png',
      'assets/characters/green/像素史莱姆-0-use_07.png',
      'assets/characters/green/像素史莱姆-0-use_08.png',
      'assets/characters/green/像素史莱姆-0-use_09.png',
      'assets/characters/green/像素史莱姆-0-use_10.png'
    ],
    duration: ANIMATION_DURATION.use_item,
    frameRate: ANIMATION_FRAMERATE.use_item,
    loop: false,
    priority: FRAME_ANIMATION_PRIORITY.USE_ITEM
  },
  
  die: {
    key: 'slime_die',
    type: 'die',
    frames: [
      'assets/characters/green/像素史莱姆-0-die_00.png',
      'assets/characters/green/像素史莱姆-0-die_01.png',
      'assets/characters/green/像素史莱姆-0-die_02.png',
      'assets/characters/green/像素史莱姆-0-die_03.png',
      'assets/characters/green/像素史莱姆-0-die_04.png',
      'assets/characters/green/像素史莱姆-0-die_05.png',
      'assets/characters/green/像素史莱姆-0-die_06.png',
      'assets/characters/green/像素史莱姆-0-die_07.png',
      'assets/characters/green/像素史莱姆-0-die_08.png',
      'assets/characters/green/像素史莱姆-0-die_09.png',
      'assets/characters/green/像素史莱姆-0-die_10.png',
      'assets/characters/green/像素史莱姆-0-die_11.png'
    ],
    duration: ANIMATION_DURATION.die,
    frameRate: ANIMATION_FRAMERATE.die,
    loop: false,
    priority: FRAME_ANIMATION_PRIORITY.DIE
  }
};

// 序列帧资源配置 - 武器攻击动画（使用史莱姆attack素材）
export const WEAPON_ANIMATION_ASSETS: Record<WeaponType, FrameAnimationConfig> = {
  SWORD: {
    key: 'slime_attack_sword',
    type: 'attack_sword',
    frames: [
      'assets/characters/green/像素史莱姆-0-atk_0.png',
      'assets/characters/green/像素史莱姆-0-atk_1.png',
      'assets/characters/green/像素史莱姆-0-atk_2.png',
      'assets/characters/green/像素史莱姆-0-atk_3.png',
      'assets/characters/green/像素史莱姆-0-atk_4.png',
      'assets/characters/green/像素史莱姆-0-atk_5.png',
      'assets/characters/green/像素史莱姆-0-atk_6.png',
      'assets/characters/green/像素史莱姆-0-atk_7.png'
    ],
    duration: ANIMATION_DURATION.attack_sword,
    frameRate: ANIMATION_FRAMERATE.attack_sword,
    loop: false,
    priority: FRAME_ANIMATION_PRIORITY.ATTACK
  },
  
  AXE: {
    key: 'slime_attack_axe',
    type: 'attack_axe',
    frames: [
      'assets/characters/green/像素史莱姆-0-atk_0.png',
      'assets/characters/green/像素史莱姆-0-atk_1.png',
      'assets/characters/green/像素史莱姆-0-atk_2.png',
      'assets/characters/green/像素史莱姆-0-atk_3.png',
      'assets/characters/green/像素史莱姆-0-atk_4.png',
      'assets/characters/green/像素史莱姆-0-atk_5.png',
      'assets/characters/green/像素史莱姆-0-atk_6.png',
      'assets/characters/green/像素史莱姆-0-atk_7.png'
    ],
    duration: ANIMATION_DURATION.attack_axe,
    frameRate: ANIMATION_FRAMERATE.attack_axe,
    loop: false,
    priority: FRAME_ANIMATION_PRIORITY.ATTACK
  },
  
  LANCE: {
    key: 'slime_attack_lance',
    type: 'attack_lance',
    frames: [
      'assets/characters/green/像素史莱姆-0-atk_0.png',
      'assets/characters/green/像素史莱姆-0-atk_1.png',
      'assets/characters/green/像素史莱姆-0-atk_2.png',
      'assets/characters/green/像素史莱姆-0-atk_3.png',
      'assets/characters/green/像素史莱姆-0-atk_4.png',
      'assets/characters/green/像素史莱姆-0-atk_5.png',
      'assets/characters/green/像素史莱姆-0-atk_6.png',
      'assets/characters/green/像素史莱姆-0-atk_7.png'
    ],
    duration: ANIMATION_DURATION.attack_lance,
    frameRate: ANIMATION_FRAMERATE.attack_lance,
    loop: false,
    priority: FRAME_ANIMATION_PRIORITY.ATTACK
  }
};

// 动画配置总集
export const FRAME_ANIMATION_CONFIG = {
  baseAnimations: BASE_ANIMATION_ASSETS,
  weaponAnimations: WEAPON_ANIMATION_ASSETS,
  
  // 获取动画配置的工具函数
  getAnimationConfig(animationType: AnimationType): FrameAnimationConfig | null {
    // 检查基础动画
    if (animationType in BASE_ANIMATION_ASSETS) {
      return BASE_ANIMATION_ASSETS[animationType as BaseAnimationType];
    }
    
    // 检查武器动画
    if (animationType.startsWith('attack_')) {
      const weaponType = animationType.replace('attack_', '').toUpperCase() as WeaponType;
      if (weaponType in WEAPON_ANIMATION_ASSETS) {
        return WEAPON_ANIMATION_ASSETS[weaponType];
      }
    }
    
    return null;
  },
  
  // 根据武器类型获取攻击动画
  getAttackAnimation(weaponType?: WeaponType): AnimationType {
    if (!weaponType) return 'rush'; // 无武器使用rush
    
    switch (weaponType) {
      case 'SWORD': return 'attack_sword';
      case 'AXE': return 'attack_axe';
      case 'LANCE': return 'attack_lance';
      default: return 'rush';
    }
  }
};

// 动画过渡配置
export const FRAME_ANIMATION_TRANSITION_CONFIG = {
  crossFadeTime: 100,          // 动画交叉淡入淡出时间(ms)
  maxSimultaneousAnimations: 20, // 最大同时播放动画数量
  defaultScale: 1.0,           // 默认缩放
  priority: {                  // 动画打断规则
    canInterrupt: {
      idle: ['walk', 'hurt', 'rush', 'use_item', 'attack_sword', 'attack_axe', 'attack_lance', 'die'],
      walk: ['hurt', 'rush', 'use_item', 'attack_sword', 'attack_axe', 'attack_lance', 'die'],
      hurt: ['die'],
      rush: ['hurt', 'die'],
      use_item: ['hurt', 'die'],
      attack_sword: ['hurt', 'die'],
      attack_axe: ['hurt', 'die'],
      attack_lance: ['hurt', 'die'],
      die: [] // 死亡动画不可被打断
    }
  }
} as const;

// 支持的游戏事件类型
export type GameEventType = keyof typeof GAME_EVENT_TO_ANIMATION;